import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/pages/SharePoster.css';
import '../styles/overrides/LogoPositionFix.css'; // Import logo positioning overrides
import Loader from '../components/Loader';
import CTABannerCarousel from '../components/CTABannerCarousel';
import { getRandomTemplateForIndustry } from '../utils/templateSelector'; // Import template selector
import { uploadPosterToS3 } from '../utils/s3Upload'; // Import S3 upload utility
import sessionTracker from '../utils/sessionTracker'; // Import session tracker
import CacheCleanup from '../utils/cacheCleanup'; // Import cache cleanup utility

const SharePoster = () => {
  const navigate = useNavigate();
  const [processedImage, setProcessedImage] = useState(null);
  const [userData, setUserData] = useState({});
  const [generatedPoster, setGeneratedPoster] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState('Preparing your Poster...');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [templatePath, setTemplatePath] = useState(null);
  const [templateLoaded, setTemplateLoaded] = useState(false); // Flag to track template loading
  const canvasRef = useRef(null);
  const preloadedTemplateRef = useRef(null); // Ref to store preloaded image
  
  // This will be replaced with actual banner data in the future
  // Add cache buster to prevent image caching issues
  const cacheBuster = `?v=${Date.now()}`;
  const [ctaBanners, setCtaBanners] = useState([
    { id: 'banner1', name: `1copy.png${cacheBuster}` },
    { id: 'banner2', name: `2copy.png${cacheBuster}` },
    { id: 'banner3', name: `3copy.png${cacheBuster}` },
    { id: 'banner4', name: `4copy.png${cacheBuster}` },
    { id: 'banner5', name: `5copy.png${cacheBuster}` }
  ]);
  
  // Note: isMobile state is declared further down in the component

  // Preload images including phone icon and contact icon when component mounts
  const preloadedPhoneIconRef = useRef(null);
  const preloadedContactIconRef = useRef(null);

  useEffect(() => {
    // Preload the phone icon to ensure it's available
    const phoneIcon = new Image();
    phoneIcon.src = '/images/PhoneIcon.png';
    phoneIcon.onload = () => {
      
      preloadedPhoneIconRef.current = phoneIcon;
    };
    phoneIcon.onerror = (err) => {
      console.error('Failed to preload phone icon:', err);
    };
    
    // Preload the contact icon to ensure it's available
    const contactIcon = new Image();
    contactIcon.src = '/images/contact.png';
    contactIcon.onload = () => {
      
      preloadedContactIconRef.current = contactIcon;
    };
    contactIcon.onerror = (err) => {
      console.error('Failed to preload contact icon:', err);
    };
  }, []);

  // Preload the template image when component mounts and userData is available
  useEffect(() => {
    const preloadTemplateImage = async () => {
      if (!userData || !userData.industry) {
        
        return; // Wait for user data to be available
      }

      try {
        setLoadingStatus('Preparing your Poster...');
        
        
        // Get template path based on industry
        const selectedTemplatePath = await getRandomTemplateForIndustry(userData.industry);
        setTemplatePath(selectedTemplatePath);
        
        
        // Preload the template image
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          
          preloadedTemplateRef.current = img;
          setTemplateLoaded(true); // Mark template as loaded
          setLoadingStatus('Generating your poster...'); // Update loading status after template is loaded
        };
        
        img.onerror = (err) => {
          console.warn(`Failed to preload template image from: ${selectedTemplatePath}`, err);
          // Fall back to Manufacturing template if the template fails to load
          const fallbackImage = new Image();
          fallbackImage.crossOrigin = 'anonymous';
          fallbackImage.src = '/images/templates/Manufacturing/Frame 15164.png';
          
          fallbackImage.onload = () => {
            
            preloadedTemplateRef.current = fallbackImage;
            setTemplateLoaded(true); // Mark template as loaded even with fallback
            setLoadingStatus('Generating your poster...'); // Update loading status after template is loaded
          };
        };
        
        img.src = selectedTemplatePath;
      } catch (error) {
        console.error('Error preloading template image:', error);
        // Ensure the process continues even with error
        setTemplateLoaded(true);
        setLoadingStatus('Generating your poster...');
      }
    };
    
    preloadTemplateImage();
  }, [userData]);  // Re-run when userData changes

  // Define generatePoster with useCallback before it's used in useEffect
  const generatePoster = useCallback(async () => {
    
    
    
    

    // Track if we're still processing to avoid duplicate states
    let isProcessing = true;
    
    // Create a timeout that will force completion if the process takes too long
    const timeoutId = setTimeout(() => {
      if (isProcessing) {
        
        setGeneratedPoster('/images/fallback-poster.png');
        
        // Use the same smooth progress approach
        setLoadingProgress(95);
        setTimeout(() => {
          setLoadingProgress(100);
          setTimeout(() => {
            setIsLoading(false);
          }, 1000);
        }, 700);
        
        isProcessing = false;
      }
    }, 15000); // 15 seconds timeout
    
    try {
      setLoadingStatus('Generating your poster...');
      
      // Simple incremental progress - start at 20%
      setLoadingProgress(20);
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      // Create a canvas to combine the template and user's image
      const canvas = canvasRef.current;
      if (!canvas) {
        console.error('Canvas ref is null');
        clearTimeout(timeoutId);
        setError('Could not create poster canvas.');
        setIsLoading(false);
        isProcessing = false;
        return;
      }
      
      // Progress to 40%
      setLoadingProgress(40);
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        console.error('Could not create canvas context');
        clearTimeout(timeoutId);
        setError('Could not create poster canvas context.');
        setIsLoading(false);
        isProcessing = false;
        return;
      }
      
      // Set canvas size - using a 3:4 aspect ratio to match the frame
      canvas.width = 900;
      canvas.height = 1200;
      
      // Progress to 60%
      setLoadingProgress(60);
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      try {
        // Load user's image
        setLoadingStatus('Generating your poster...');
        const userImg = new Image();
        userImg.crossOrigin = 'anonymous';
        userImg.src = processedImage;
        
        
        
        // Progress to 80%
        setLoadingProgress(80); 
        await new Promise(resolve => setTimeout(resolve, 1200));
        
        // Progress to 90% and stay there for the rest of processing
        setLoadingProgress(90);
        
        // Add a logging function to track the image loading process
        const trackImageLoading = async (img, description) => {
          return new Promise((resolve) => {
            const checkLoaded = () => {
              if (img.complete) {
                
                resolve(true);
              } else {
                
                setTimeout(checkLoaded, 200);
              }
            };
            checkLoaded();
          });
        };
        
        // Track user image loading
        await trackImageLoading(userImg, 'User image');
        
        // Load the template image based on user's industry
        // Use the selected template path if available, otherwise fall back to Manufacturing template
        const templateSrc = templatePath || `${window.location.origin}/images/templates/Manufacturing/Frame 15164.png`;
          
        
        
        setLoadingStatus('Generating your poster...');
        let templateImg;
        
        // Use preloaded template image if available
        if (preloadedTemplateRef.current && 
            preloadedTemplateRef.current.complete && 
            preloadedTemplateRef.current.naturalHeight !== 0) {
          
          templateImg = preloadedTemplateRef.current;
        } else {
          // Otherwise load a new image
          templateImg = new Image();
          templateImg.crossOrigin = 'anonymous';
          templateImg.src = templateSrc;
          
          // Add error handling for template image loading
          templateImg.onerror = () => {
            console.error('Failed to load template image, trying alternate path');
            // Try without window.location.origin as a fallback
            const srcWithoutOrigin = templateSrc.replace(`${window.location.origin}`, '');
            templateImg.src = srcWithoutOrigin;
            
            // If that also fails, try a relative path
            templateImg.onerror = () => {
              console.error('Still failed to load template image, using default fallback');
              templateImg.src = '/images/templates/Manufacturing/Frame 15164.png';
            };
          };
        }
        
        // Wait for all images to load with timeout
        setLoadingStatus('Generating your poster...');
        try {
          await Promise.race([
            Promise.all([
              new Promise((resolve, reject) => {
                userImg.onload = resolve;
                userImg.onerror = () => {
                  console.error('Failed to load user image');
                  resolve(); // Continue even if image fails
                };
                // Set safety timeout
                setTimeout(() => {
                  if (!userImg.complete) resolve();
                }, 5000);
              }),
              new Promise((resolve, reject) => {
                templateImg.onload = resolve;
                templateImg.onerror = () => {
                  console.error('Failed to load template image');
                  resolve(); // Continue even if image fails
                };
                // Set safety timeout
                setTimeout(() => {
                  if (!templateImg.complete) resolve();
                }, 5000);
              })
            ]),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Image loading timeout')), 10000))
          ]).catch(error => {
            console.warn('Image loading error or timeout:', error);
            // Keep going despite timeout
          });
        } catch (imageError) {
          console.error('Error loading images:', imageError);
          // Continue with what we have
        }
        
        // Draw the template (Bumrah image) as background
        setLoadingStatus('Generating your poster...');
        if (templateImg.complete && templateImg.naturalHeight !== 0) {
          try {
            // Draw the template on the full canvas
            ctx.drawImage(templateImg, 0, 0, canvas.width, canvas.height);
            
            
            // Now place the user's image to the left of Bumrah with same height as Bumrah
            if (userImg.complete && userImg.naturalHeight !== 0) {
              try {
                // CRITICAL POSITIONING FIX:
                // Ensure the full image is visible without cutoffs
                // Image shifted 30px to the right to prevent left side from being cut by poster boundary
                
                // Position image to cover left side of poster (side by side with template model)
                const containerWidth = canvas.width * 0.50; // Increased from 0.48 to 0.50
                const containerHeight = canvas.height * 0.64; // Increased from 0.61 to 0.64
                
                // Position at bottom left corner for perfect alignment
                const userX = 8; // Moved right from -2 to 8 (10px more to the right)
                const userY = canvas.height - containerHeight + 29; // Keep the same vertical position
                
                // Set up the offscreen canvas with these dimensions
                const offscreenCanvas = document.createElement('canvas');
                offscreenCanvas.width = containerWidth;
                offscreenCanvas.height = containerHeight;
                const offCtx = offscreenCanvas.getContext('2d');
                
                
                
                // Clear canvas
                offCtx.clearRect(0, 0, containerWidth, containerHeight);
                
                try {
                  
                  
                  // SIMPLEST SOLUTION: Create a new image directly from processed image URL
                  const directImage = new Image();
                  directImage.crossOrigin = "anonymous";
                  directImage.src = processedImage;
                  
                  // Wait for image to load
                  await new Promise((resolve) => {
                    if (directImage.complete) {
                      resolve();
                    } else {
                      directImage.onload = resolve;
                    }
                  });
                  
                  
                  
                  // Calculate sizing to ensure full image is visible
                  const imgAspectRatio = directImage.width / directImage.height;
                  const containerAspectRatio = containerWidth / containerHeight;
                  
                  let drawWidth, drawHeight, offsetX = 0, offsetY = 0;
                  
                  // Use contain approach to ensure entire image is visible
                  if (imgAspectRatio > containerAspectRatio) {
                    // Image is wider - fit to width and center vertically
                    drawWidth = containerWidth;
                    drawHeight = containerWidth / imgAspectRatio;
                    offsetX = 0;
                    offsetY = (containerHeight - drawHeight) / 2;
                  } else {
                    // Image is taller - fit to height and center horizontally
                    drawHeight = containerHeight;
                    drawWidth = containerHeight * imgAspectRatio;
                    offsetX = (containerWidth - drawWidth) / 2;
                    offsetY = 0;
                  }
                  
                  // Apply margin by scaling down
                  const scaleFactor = 0.98; // Increased from 0.96 to 0.98 to maximize image size within smaller container
                  drawWidth *= scaleFactor;
                  drawHeight *= scaleFactor;
                  
                  // Recenter
                  offsetX = (containerWidth - drawWidth) / 2;
                  offsetY = (containerHeight - drawHeight) / 2;
                  
                  
                  
                  // Draw the direct image to the offscreen canvas
                  offCtx.drawImage(
                    directImage, 
                    0, 0, directImage.width, directImage.height,
                    offsetX, offsetY, drawWidth, drawHeight
                  );
                  
                  // Now draw the offscreen canvas to the main canvas
                  
                  ctx.drawImage(offscreenCanvas, userX, userY, containerWidth, containerHeight);
                  
                } catch (error) {
                  console.error('Error processing image:', error);
                }
                
                // Black tint has been removed as requested
                
                // 3. Apply basic image enhancement (contrast and brightness)
                try {
                  // Get image data for manipulation
                  const imageData = offCtx.getImageData(0, 0, containerWidth, containerHeight);
                  const data = imageData.data;
                  
                  // Increase contrast and brightness slightly
                  const contrast = 1.1; // Subtle contrast boost
                  const brightness = 5;  // Slight brightness boost
                  
                  for (let i = 0; i < data.length; i += 4) {
                    // Apply contrast and brightness
                    data[i] = Math.min(255, Math.max(0, ((data[i] - 128) * contrast) + 128 + brightness));          // red
                    data[i + 1] = Math.min(255, Math.max(0, ((data[i + 1] - 128) * contrast) + 128 + brightness));  // green
                    data[i + 2] = Math.min(255, Math.max(0, ((data[i + 2] - 128) * contrast) + 128 + brightness));  // blue
                  }
                  
                  // Put the enhanced image data back
                  offCtx.putImageData(imageData, 0, 0);
                  
                } catch (enhanceError) {
                  console.error('Error enhancing image:', enhanceError);
                  // Continue with unenhanced image if enhancement fails
                }
                
                // Now draw the finalized offscreen canvas to the main canvas at the correct position
                try {
                  
                  
                  // Draw the offscreen canvas to the main canvas
                  ctx.drawImage(offscreenCanvas, userX, userY, containerWidth, containerHeight);
                  
                } catch (drawError) {
                  console.error('Error drawing to main canvas:', drawError);
                  
                  // Try a fallback approach if needed
                  try {
                    
                    ctx.drawImage(userImg, userX, userY, containerWidth, containerHeight);
                  } catch (fallbackError) {
                    console.error('Fallback drawing also failed:', fallbackError);
                  }
                }
              } catch (imageDrawError) {
                console.error('Error drawing user image:', imageDrawError);
                // Continue without user image placement
              }
            }
          } catch (e) {
            console.error('Error drawing template:', e);
            // Try to reload the image one more time with a direct approach
            try {
              
              // Create a new image object and try again
              const retryImg = new Image();
              retryImg.crossOrigin = 'anonymous';
              retryImg.src = '/images/templates/Manufacturing/Frame 15164.png'; // Use Manufacturing template
              
              // Wait briefly for the image to load
              await new Promise((resolve) => {
                retryImg.onload = resolve;
                retryImg.onerror = resolve; // Continue even if error
                setTimeout(resolve, 2000); // Max wait of 2 seconds
              });
              
              if (retryImg.complete && retryImg.naturalHeight !== 0) {
                ctx.drawImage(retryImg, 0, 0, canvas.width, canvas.height);
                
              } else {
                throw new Error('Retry failed, using fallback color');
              }
            } catch (retryError) {
              console.error('Retry also failed:', retryError);
              ctx.fillStyle = '#0a1a34'; // Navy blue background as fallback
              ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
          }
        } else {
          // Fallback: fill with a color
          console.warn('Template image failed to load, using fallback background');
          ctx.fillStyle = '#0a1a34'; // Navy blue background as fallback
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Draw L&T Finance logo as a fallback
          try {
            const logoImg = new Image();
            logoImg.crossOrigin = 'anonymous';
            logoImg.src = `${window.location.origin}/images/l&tlogo.png?v=20241118`;
            
            // Wait briefly for the logo to load
            await new Promise((resolve) => {
              logoImg.onload = resolve;
              logoImg.onerror = resolve; // Continue even if error
              setTimeout(resolve, 1000); // Max wait of 1 second
            });
            
            if (logoImg.complete && logoImg.naturalHeight !== 0) {
              // Draw logo at the top center
              const logoWidth = canvas.width * 0.3;
              const logoHeight = logoWidth * (logoImg.height / logoImg.width);
              ctx.drawImage(
                logoImg,
                (canvas.width - logoWidth) / 2,
                canvas.height * 0.1,
                logoWidth,
                logoHeight
              );
              
            }
          } catch (logoError) {
            console.error('Error drawing fallback logo:', logoError);
          }
        }
        
        setLoadingStatus('Generating your poster...');
        
        try {
          // Add tagline at the top
          // Remove any leftover asterisks before processing the tagline
          // Ensure tagline is properly retrieved and preserved
          let tagline = '';
          if (userData && userData.tagline) {
            // Use the exact tagline from userData without adding extra words
            tagline = userData.tagline.trim();
            
          } else {
            tagline = 'I transform businesses with innovation and expertise';
            
          }
          
          // Now draw Group 30a image on the right side with higher z-index
          try {
            const group30aImg = new Image();
            group30aImg.crossOrigin = 'anonymous';
            group30aImg.src = '/images/section1/Group 30a (1).png'; // Correct path with section1 folder
            
            
            
            // Wait for image to load
            await new Promise((resolve) => {
              if (group30aImg.complete) {
                
                resolve();
              } else {
                group30aImg.onload = () => {
                  
                  resolve();
                };
                group30aImg.onerror = (err) => {
                  console.error('Failed to load Group 30a image in final position:', err);
                  resolve(); // Continue even if image fails
                };
                setTimeout(() => resolve(), 3000); // Safety timeout
              }
            });
            
            if (group30aImg.complete && group30aImg.naturalHeight !== 0) {
              // Position on the right side of the canvas
              const rightImageWidth = canvas.width * 0.46; // Keep same width
              const rightImageHeight = canvas.height * 0.63; // Keep same height
              const rightImageX = canvas.width - rightImageWidth - 40; // Move 15px more to the left (from -25 to -40)
              const rightImageY = canvas.height - rightImageHeight + 50; // Keep same Y position
              
              
              
              // Draw the image on top of everything
              ctx.drawImage(group30aImg, rightImageX, rightImageY, rightImageWidth, rightImageHeight);
              
            }
          } catch (rightImageError) {
            console.error('Error drawing Group 30a image in final position:', rightImageError);
          }
          
          // Tagline styling - using Poppins font and increased font size by 3.5
          ctx.fillStyle = 'white'; // Always use white color for tagline text
          ctx.font = 'bold 44px Poppins, sans-serif'; // Reduced from 45.5px to 44px
          ctx.textAlign = 'left';
          
          // Calculate vertical spacing for taglines - adjusted for better positioning
          const taglineStartY = canvas.height * 0.09; // Moved up slightly from 0.10 to 0.09
          const taglineLineHeight = 50; // Reduced from 55 to 50 for tighter line spacing
          
          // Enhanced function to handle drawing text with certain words in bold
          const drawTextWithBoldedWords = (line, x, y, keywords) => {
            // First check if there are any asterisks in the line to extract immediate highlights
            const asteriskMatches = line.match(/\*(.*?)\*/g);
            if (asteriskMatches) {
              // Extract words from the asterisks
              const immediateHighlights = asteriskMatches.map(match => match.replace(/\*/g, '').trim());
              // Add to keywords array if not already there
              immediateHighlights.forEach(word => {
                if (word && !keywords.includes(word)) {
                  keywords.push(word);
                }
              });
            }
            
            // Remove all asterisks for clean processing
            const cleanLine = line.replace(/\*/g, '');
            // Split on spaces for word-by-word processing
            const segments = cleanLine.split(' ');
            let currentX = x;
            
            segments.forEach((segment, index) => {
              if (!segment || segment.trim().length === 0) return;
              
              // Enhanced keyword detection - check for exact match, partial match, or plurals
              const isKeyword = keywords.some(keyword => {
                // Skip empty keywords
                if (!keyword || keyword.trim().length === 0) return false;
                
                const segmentLower = segment.toLowerCase().trim();
                const keywordLower = keyword.toLowerCase().trim();
                
                // Exact match
                if (segmentLower === keywordLower) return true;
                
                // Word boundary check for better matching
                const segmentWithBoundaries = ` ${segmentLower} `;
                const keywordWithBoundaries = ` ${keywordLower} `;
                
                // Check if segment is part of the keyword (with word boundaries)
                if (` ${segment} `.toLowerCase().includes(` ${keyword} `.toLowerCase())) return true;
                
                // Segment contains keyword (more flexible matching)
                if (segmentLower.includes(keywordLower)) {
                  // Check for word boundaries or if it's at the start/end
                  if (segmentLower === keywordLower || 
                      segmentLower.startsWith(`${keywordLower} `) || 
                      segmentLower.endsWith(` ${keywordLower}`) || 
                      segmentLower.includes(` ${keywordLower} `)) {
                    return true;
                  }
                }
                
                // Keyword contains segment (for multi-word keywords)
                if (keywordLower.includes(segmentLower)) {
                  // Check for word boundaries or if it's at the start/end
                  if (keywordLower === segmentLower || 
                      keywordLower.startsWith(`${segmentLower} `) || 
                      keywordLower.endsWith(` ${segmentLower}`) || 
                      keywordLower.includes(` ${segmentLower} `)) {
                    return true;
                  }
                }
                
                // Improved plural and singular form checks
                if ((segmentLower + 's' === keywordLower) || 
                    (keywordLower + 's' === segmentLower) || 
                    (segmentLower.endsWith('s') && segmentLower.slice(0, -1) === keywordLower) || 
                    (keywordLower.endsWith('s') && keywordLower.slice(0, -1) === segmentLower) ||
                    (segmentLower.endsWith('es') && segmentLower.slice(0, -2) === keywordLower) ||
                    (keywordLower.endsWith('es') && keywordLower.slice(0, -2) === segmentLower)) {
                  return true;
                }
                
                return false;
              });
              
              // Debug information for keywords
              if (process.env.NODE_ENV !== 'production') {
                // Keyword match debugging removed
              }
              
              // Set appropriate font weight and style for better differentiation
              if (isKeyword) {
                ctx.font = 'bold 44.5px Poppins, sans-serif'; // Slightly larger for bold words
                ctx.fillStyle = '#FFFFFF'; // Pure white for bold words
                // Add text shadow for extra emphasis on keywords
                ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
                ctx.shadowBlur = 3;
                ctx.shadowOffsetX = 1;
                ctx.shadowOffsetY = 1;
              } else {
                ctx.font = '43.5px Poppins, sans-serif';
                ctx.fillStyle = '#F0F0F0'; // Slightly less bright for non-bold words
                // Reset shadow for non-bold words
                ctx.shadowColor = 'transparent';
                ctx.shadowBlur = 0;
                ctx.shadowOffsetX = 0;
                ctx.shadowOffsetY = 0;
              }
              
              // Draw the word
              ctx.fillText(segment, currentX, y);
              
              // Move x position for next word
              const wordWidth = ctx.measureText(segment).width;
              const spaceWidth = ctx.measureText(' ').width;
              currentX += wordWidth + spaceWidth;
            });
          };
          
          // Split the tagline into multiple lines if needed
          let taglineLines = [];
          const maxLineLength = 32; // Reduced from 34 to 32 to decrease tagline width
          
          if (tagline.length > maxLineLength) {
            // Break into multiple lines
            const words = tagline.split(' ');
            let currentLine = '';
            
            words.forEach((word) => {
              if ((currentLine + word).length < maxLineLength) {
                currentLine += (currentLine ? ' ' : '') + word;
              } else {
                taglineLines.push(currentLine);
                currentLine = word;
              }
            });
            
            if (currentLine) {
              taglineLines.push(currentLine);
            }
          } else {
            taglineLines = [tagline];
          }
          
          // Get selected keywords from session storage (or default to empty array)
          let selectedKeywords = [];
          let highlightedKeywords = [];
          
          try {
            // Get the explicitly selected keywords
            const storedKeywords = sessionStorage.getItem('selectedKeywords');
            if (storedKeywords) {
              selectedKeywords = JSON.parse(storedKeywords);
              
            }
            
            // Also get any highlighted keywords extracted from asterisks
            const storedHighlightedKeywords = sessionStorage.getItem('highlightedKeywords');
            if (storedHighlightedKeywords) {
              highlightedKeywords = JSON.parse(storedHighlightedKeywords);
              
            }
            
            // Combine both sources for a comprehensive list of words to bold
            selectedKeywords = [...new Set([...selectedKeywords, ...highlightedKeywords])];
            
            
            // Ensure keywords are properly normalized for matching (lowercase, trimmed)
            selectedKeywords = selectedKeywords.map(keyword => keyword.trim()).filter(keyword => keyword.length > 0);
            
          } catch (e) {
            console.error('Error parsing keywords:', e);
          }
          
          // Draw each line with keywords bolded - moved right by 3px and reduced width from right by 3px
          taglineLines.forEach((line, index) => {
            drawTextWithBoldedWords(line, 68, taglineStartY + (index * taglineLineHeight), selectedKeywords);
          });
          
          // Position for company info circles - pushed further up to match logo alignment
          const circleY = canvas.height * 0.28 - 3; // Further reduced by 3px as requested
          
          // Yellow circle backgrounds removed as requested
          
          // Draw contact icon in the first circle using the preloaded contact.png
          const contactX = 83;
          const contactY = circleY;
          
          // Use the preloaded contact icon if available, otherwise create a fallback
          if (preloadedContactIconRef.current) {
            
            
            // Draw the contact icon with slightly reduced size
            ctx.drawImage(
              preloadedContactIconRef.current,
              contactX - 25, // Position centered with 50px size
              contactY - 25, // Position centered with 50px size
              50,           // Width (reduced to 50px for better proportion)
              50            // Height (reduced to 50px for better proportion)
            );
          } else {
            // Fallback if contact icon fails to load - draw a proportional person icon
            console.warn('Preloaded contact icon not available, drawing fallback');
            ctx.fillStyle = 'white';
            
            // Draw the head (circle) - proportional to 50px icon
            ctx.beginPath();
            ctx.arc(contactX, contactY - 6, 10, 0, Math.PI * 2);
            ctx.fill();
            
            // Draw the body (rounded rectangle) - proportional
            ctx.beginPath();
            ctx.moveTo(contactX - 12, contactY + 8);  // Left shoulder
            ctx.lineTo(contactX + 12, contactY + 8);  // Right shoulder
            ctx.lineTo(contactX + 12, contactY + 20); // Bottom right
            ctx.arc(contactX, contactY + 20, 12, 0, Math.PI, true); // Bottom rounded edge
            ctx.lineTo(contactX - 12, contactY + 8);  // Back to left shoulder
            ctx.fill();
          }
          
          // Draw phone icon in the second circle - moved 3px to the right
          const phoneX = 83;
          const phoneY = circleY + 85;
          
          // Use the preloaded phone icon if available, otherwise create a new one
          if (preloadedPhoneIconRef.current) {
            // Use the preloaded icon which is guaranteed to be loaded
            
            const iconSize = 50; // Reduced to 50px for better proportion
            ctx.drawImage(
              preloadedPhoneIconRef.current,
              phoneX - iconSize/2, // Center horizontally
              phoneY - iconSize/2, // Center vertically
              iconSize,
              iconSize
            );
          } else {
            // Fallback if preloaded icon is not available
            console.warn('Preloaded icon not available, drawing fallback');
            // Draw a larger fallback phone icon (no yellow background)
            ctx.fillStyle = '#FFFFFF'; // White color for visibility
            
            // Draw phone handset proportional to 50px icon size
            ctx.fillStyle = '#000000';
            ctx.lineWidth = 3;
            ctx.lineCap = 'round';
            
            // Earpiece (top circle) - proportional
            ctx.beginPath();
            ctx.arc(phoneX - 6, phoneY - 4, 4, 0, Math.PI * 2);
            ctx.fill();
            
            // Mouthpiece (bottom circle) - proportional
            ctx.beginPath();
            ctx.arc(phoneX + 6, phoneY + 4, 4, 0, Math.PI * 2);
            ctx.fill();
            
            // Connect the two parts with a line - proportional
            ctx.beginPath();
            ctx.strokeStyle = '#000000';
            ctx.moveTo(phoneX - 6, phoneY - 4);
            ctx.lineTo(phoneX + 6, phoneY + 4);
            ctx.stroke();
          }
          
          // Draw company info text - properly aligned vertically with icons
          ctx.fillStyle = 'white';
          ctx.font = 'bold 28px Poppins, sans-serif';
          ctx.textAlign = 'left';
          
          // Get user name
          const userName = userData.name || 'Your Name';
          
          // Get company name - TRUST EXISTING COMPANY DATA
          // If company data exists in userData, show it (regardless of hardcoded list)
          // Only use hardcoded list as fallback validation
          const hasCompanyDataInUserData = userData.companyName && userData.companyName.trim() !== '';
          const shouldShowCompanyByList = CacheCleanup.shouldHaveCompanyData(userData.phoneNumber);
          const companyName = hasCompanyDataInUserData ? userData.companyName.trim() : '';
          
          // Log for debugging
          console.log('🏢 SharePoster - Company name check:', {
            rawCompanyName: userData.companyName,
            finalCompanyName: companyName,
            hasCompanyDataInUserData: hasCompanyDataInUserData,
            shouldShowCompanyByList: shouldShowCompanyByList,
            willShowCompany: companyName !== '',
            phoneNumber: userData.phoneNumber
          });
          
          // 🚨 DETAILED DEBUG: Show exactly what userData contains
          console.log('🔍 SharePoster - DETAILED userData DEBUG:', {
            fullUserData: userData,
            companyNameType: typeof userData.companyName,
            companyNameValue: userData.companyName,
            companyNameLength: userData.companyName ? userData.companyName.length : 0,
            hasCompanyName: !!userData.companyName,
            companyNameTrimmed: userData.companyName ? userData.companyName.trim() : 'N/A',
            isEmpty: userData.companyName === '' || userData.companyName === null || userData.companyName === undefined
          });
          
          // 🚨 FINAL COMPANY NAME DEBUG: Show what will be rendered
          console.log('🎨 SharePoster - FINAL RENDER DEBUG:', {
            willRenderCompany: companyName !== '',
            renderText: companyName,
            renderTextLength: companyName.length,
            condition1: hasCompanyDataInUserData,
            condition2: userData.companyName && userData.companyName.trim() !== ''
          });
          
          // Get phone number to calculate maximum available width
          let phoneNumber = userData.phoneNumber || 'Your Phone Number';
          if (phoneNumber.includes('+91')) {
            phoneNumber = phoneNumber.replace('+91', '').trim();
          }
          
          // Calculate maximum width for text elements before the white line
          const maxTextWidth = canvas.width * 0.3; // Maximum 30% of canvas width for text
          
          // Calculate username positioning based on company name presence
          // If no company name, center username with user icon; if company name exists, keep current higher position
          const userNameYOffset = companyName ? 0 : 8; // Perfect center alignment with user icon when no company
          
          // Check if name needs to be wrapped
          const nameWidth = ctx.measureText(userName).width;
          
          if (nameWidth > maxTextWidth) {
            // Name is too long, need to split it
            const words = userName.split(' ');
            let line1 = '';
            let line2 = '';
            let currentWidth = 0;
            
            // Distribute words between lines
            for (const word of words) {
              const wordWidth = ctx.measureText(word + ' ').width;
              
              if (currentWidth + wordWidth <= maxTextWidth) {
                line1 += word + ' ';
                currentWidth += wordWidth;
              } else {
                line2 += word + ' ';
              }
            }
            
            // Trim extra spaces
            line1 = line1.trim();
            line2 = line2.trim();
            
            // Draw the name in two lines - position adjusts based on company name presence
            if (companyName) {
              // With company - use original positioning
              ctx.fillText(line1, 133, circleY - 14 + userNameYOffset); // First line
              ctx.fillText(line2, 133, circleY + 16 + userNameYOffset); // Second line
            } else {
              // No company - center the two lines around the user icon
              ctx.fillText(line1, 133, circleY - 8); // First line above center
              ctx.fillText(line2, 133, circleY + 12); // Second line below center
            }
            
            // Display company name below the name (if it exists) with line breaking support
            if (companyName) {
              ctx.font = 'normal 18px Poppins, sans-serif'; // Reduced from 22px to 18px
              
              // Check if company name needs line breaking
              const companyWidth = ctx.measureText(companyName).width;
              const maxCompanyWidth = maxTextWidth * 1.1; // Allow slightly more width for company name
              
              if (companyWidth > maxCompanyWidth) {
                // Company name is too long, split into two lines
                const companyWords = companyName.split(' ');
                let companyLine1 = '';
                let companyLine2 = '';
                let companyCurrentWidth = 0;
                
                for (const word of companyWords) {
                  const wordWidth = ctx.measureText(word + ' ').width;
                  
                  if (companyCurrentWidth + wordWidth <= maxCompanyWidth) {
                    companyLine1 += word + ' ';
                    companyCurrentWidth += wordWidth;
                  } else {
                    companyLine2 += word + ' ';
                  }
                }
                
                // Trim extra spaces
                companyLine1 = companyLine1.trim();
                companyLine2 = companyLine2.trim();
                
                // Draw company name in two lines
                ctx.fillText(companyLine1, 133, circleY + 47); // First line below user name
                ctx.fillText(companyLine2, 133, circleY + 67); // Second line
              } else {
                // Company name fits on single line
                ctx.fillText(companyName, 133, circleY + 47); // Position below the second line
              }
            }
          } else {
            // Name fits on a single line - position adjusts based on company name presence
            if (companyName) {
              // With company - use offset positioning
              ctx.fillText(userName, 133, circleY - 1 + userNameYOffset);
            } else {
              // No company - perfect center alignment with user icon
              ctx.fillText(userName, 133, circleY + 8); // Perfect center with user icon (accounting for text baseline)
            }
            
            // Display company name below the name (if it exists) with line breaking support
            if (companyName) {
              ctx.font = 'normal 18px Poppins, sans-serif'; // Reduced from 22px to 18px
              
              // Check if company name needs line breaking
              const companyWidth = ctx.measureText(companyName).width;
              const maxCompanyWidth = maxTextWidth * 1.1; // Allow slightly more width for company name
              
              if (companyWidth > maxCompanyWidth) {
                // Company name is too long, split into two lines
                const companyWords = companyName.split(' ');
                let companyLine1 = '';
                let companyLine2 = '';
                let companyCurrentWidth = 0;
                
                for (const word of companyWords) {
                  const wordWidth = ctx.measureText(word + ' ').width;
                  
                  if (companyCurrentWidth + wordWidth <= maxCompanyWidth) {
                    companyLine1 += word + ' ';
                    companyCurrentWidth += wordWidth;
                  } else {
                    companyLine2 += word + ' ';
                  }
                }
                
                // Trim extra spaces
                companyLine1 = companyLine1.trim();
                companyLine2 = companyLine2.trim();
                
                // Draw company name in two lines
                ctx.fillText(companyLine1, 133, circleY + 30); // First line below user name
                ctx.fillText(companyLine2, 133, circleY + 50); // Second line
              } else {
                // Company name fits on single line
                ctx.fillText(companyName, 133, circleY + 30); // Position below the name
              }
            }
          }
          
          // Phone number display - DYNAMIC POSITION (adjusts based on company name length)
          ctx.font = 'bold 28px Poppins, sans-serif';
          
          // Calculate phone number Y position based on company name layout
          let phoneNumberY = circleY + 85; // Default position
          
          if (companyName) {
            // Check if company name needs multiple lines to determine phone position
            ctx.font = 'normal 18px Poppins, sans-serif'; // Set company font to measure
            const companyWidth = ctx.measureText(companyName).width;
            const maxCompanyWidth = maxTextWidth * 1.1;
            
            if (companyWidth > maxCompanyWidth) {
              // Company name uses two lines, push phone number down more
              phoneNumberY = circleY + 95; // Additional spacing for two-line company name
            } else {
              // Company name uses one line, standard spacing
              phoneNumberY = circleY + 85; // Standard spacing
            }
            
            // Reset font for phone number
            ctx.font = 'bold 28px Poppins, sans-serif';
          } else {
            // No company name - center phone number perfectly with phone icon
            phoneNumberY = circleY + 85 + 8; // Perfect center alignment with phone icon (circleY + 85 is icon center, +8 for text baseline)
          }
          
          // Phone number positioned dynamically based on company name layout
          ctx.fillText(phoneNumber, 133, phoneNumberY);
          
          // Add vertical white line - positioned to align with text layout
          const lineX = 133 + 190 + ctx.measureText(phoneNumber).width; // Position after phone number
          const lineStartY = circleY - 41; // Start position
          const lineEndY = phoneNumberY + 14; // End position adjusted to phone number position
          
          // Draw the white vertical line
          ctx.beginPath();
          ctx.moveTo(lineX, lineStartY);
          ctx.lineTo(lineX, lineEndY);
          ctx.lineWidth = 2;
          ctx.strokeStyle = 'white';
          ctx.stroke();
          
          // Name at bottom removed as requested
        } catch (textError) {
          console.error('Error adding text to poster:', textError);
        }
        
        // Add yellow strip at the bottom with URL as requested by the client
        try {
          // Create yellow strip at bottom with reduced width and rounded corners
          const stripHeight = 40; // Height of the strip in pixels
          const stripMargin = 100; // Margin from each side (increased from 70px to 100px as requested)
          const cornerRadius = 10; // Radius for rounded corners
          
          ctx.fillStyle = '#FFC107'; // Yellow color
          
          // Draw rounded rectangle for the strip
          ctx.beginPath();
          // Start from top-left corner with radius
          ctx.moveTo(stripMargin + cornerRadius, canvas.height - stripHeight);
          // Top edge
          ctx.lineTo(canvas.width - stripMargin - cornerRadius, canvas.height - stripHeight);
          // Top-right corner
          ctx.arcTo(canvas.width - stripMargin, canvas.height - stripHeight, canvas.width - stripMargin, canvas.height - stripHeight + cornerRadius, cornerRadius);
          // Right edge
          ctx.lineTo(canvas.width - stripMargin, canvas.height);
          // Bottom-right corner (no rounding)
          ctx.lineTo(stripMargin, canvas.height);
          // Bottom-left corner (no rounding)
          ctx.lineTo(stripMargin, canvas.height - stripHeight + cornerRadius);
          // Top-left corner
          ctx.arcTo(stripMargin, canvas.height - stripHeight, stripMargin + cornerRadius, canvas.height - stripHeight, cornerRadius);
          ctx.closePath();
          ctx.fill();
          
          // Add URL text to the strip
          ctx.fillStyle = '#000000'; // Black text (changed from white for better contrast on yellow)
          ctx.font = 'bold 30px Poppins, sans-serif'; // Significantly increased font size for better visibility
          ctx.textAlign = 'center';
          ctx.fillText('Visit: www.LTFGAMECHANGER.in', canvas.width / 2, canvas.height - stripHeight/2 + 8); // Centered vertically in the strip
          
          
        } catch (stripError) {
          console.error('Error adding URL strip to poster:', stripError);
        }
        
        // Convert canvas to image
        setLoadingStatus('Generating your poster...');
        // Progress to 95% and pause there while generating the image
        await new Promise(resolve => setTimeout(resolve, 1200));
        setLoadingProgress(95);
        
        try {
          const posterUrl = canvas.toDataURL('image/png');
          if (posterUrl) {
            // Upload to S3 in background (don't wait for it to complete)
            uploadPosterToS3(posterUrl, userData)
              .then(result => {
                if (result.success) {
                  // S3 upload successful - track the S3 URL
                  sessionTracker.trackPosterGeneration({
                    s3Url: result.url,
                    status: 'Success'
                  });
                  console.log('Poster uploaded successfully to S3:', result.url);
                } else {
                  console.error('Failed to upload poster to S3:', result.error);
                  // Track failed poster generation
                  sessionTracker.trackPosterGeneration({
                    s3Url: null,
                    status: 'Failed'
                  });
                }
              })
              .catch(error => {
                console.error('S3 upload error:', error);
                // Track failed poster generation
                sessionTracker.trackPosterGeneration({
                  s3Url: null,
                  status: 'Failed'
                });
              });
            
            setGeneratedPoster(posterUrl);
          } else {
            throw new Error('Failed to generate poster image');
          }
        } catch (dataUrlError) {
          console.error('Error creating data URL:', dataUrlError);
          throw dataUrlError;
        }
        
      } catch (renderError) {
        console.error('Error rendering poster:', renderError);
        // Fallback to simpler layout if rendering fails
        
        try {
          setLoadingStatus('Generating your poster...');
          // Clear canvas and start with a plain background
          ctx.fillStyle = '#333333';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Add text details
          ctx.fillStyle = 'white';
          ctx.font = 'bold 36px Poppins, sans-serif';
          ctx.textAlign = 'center';
          
          // Display user name
          ctx.fillText(userData.name || 'Your Name', canvas.width / 2, 200);
          
          // Display company name if it exists - TRUST EXISTING COMPANY DATA
          const hasCompanyData = userData.companyName && userData.companyName.trim() !== '';
          const fallbackCompanyName = hasCompanyData ? userData.companyName.trim() : '';
          
          if (fallbackCompanyName) {
            ctx.font = 'normal 18px Poppins, sans-serif'; // Reduced from 28px to 18px for consistency
            
            // Check if company name needs line breaking in fallback poster
            const companyWidth = ctx.measureText(fallbackCompanyName).width;
            const maxFallbackWidth = canvas.width - 100; // Allow some margin
            
            if (companyWidth > maxFallbackWidth) {
              // Split company name into multiple lines
              const words = fallbackCompanyName.split(' ');
              let line1 = '';
              let line2 = '';
              let currentWidth = 0;
              
              for (const word of words) {
                const wordWidth = ctx.measureText(word + ' ').width;
                
                if (currentWidth + wordWidth <= maxFallbackWidth) {
                  line1 += word + ' ';
                  currentWidth += wordWidth;
                } else {
                  line2 += word + ' ';
                }
              }
              
              // Trim and draw both lines
              line1 = line1.trim();
              line2 = line2.trim();
              
              ctx.fillText(line1, canvas.width / 2, 240);
              ctx.fillText(line2, canvas.width / 2, 260);
            } else {
              // Single line company name
              ctx.fillText(fallbackCompanyName, canvas.width / 2, 240);
            }
          }
          
          ctx.font = '24px Poppins, sans-serif';
          
          // Calculate industry position based on company name layout
          let industryY = 250; // Default when no company
          if (fallbackCompanyName) {
            const companyWidth = ctx.measureText(fallbackCompanyName).width;
            const maxFallbackWidth = canvas.width - 100;
            
            if (companyWidth > maxFallbackWidth) {
              // Company uses two lines, push industry down more
              industryY = 310;
            } else {
              // Company uses one line, standard position
              industryY = 290;
            }
          }
          
          ctx.fillText(userData.industry || 'Your Industry', canvas.width / 2, industryY);
          
          // Format phone number to remove +91 if present
          let phoneNumber = userData.phoneNumber || 'Your Phone Number';
          if (phoneNumber.includes('+91')) {
            phoneNumber = phoneNumber.replace('+91', '').trim();
          }
          
          // Draw phone number with icon
          ctx.font = '24px Poppins, sans-serif';
          
          // Set up position for phone icon and text - dynamic based on industry position
          const iconX = canvas.width / 2 - ctx.measureText(phoneNumber).width / 2 - 30;
          const iconY = industryY + 60; // Position below industry with consistent spacing
          
          // Draw phone icon using preloaded image if available
          if (preloadedPhoneIconRef.current) {
            
            const iconSize = 24; // Smaller size for fallback poster
            // Draw the icon
            ctx.drawImage(
              preloadedPhoneIconRef.current,
              iconX - iconSize/2,
              iconY - iconSize/2 - 8, // Adjust vertical position to align with text
              iconSize,
              iconSize
            );
            
            // Draw the phone number text next to icon
            ctx.fillText(phoneNumber, canvas.width / 2, iconY);
          } else {
            // Fallback if preloaded icon is not available
            console.warn('Preloaded icon not available in fallback poster, using text only');
            // Just show the phone number without an icon
            ctx.fillText(phoneNumber, canvas.width / 2, iconY);
          }
          
          // Add tagline with Poppins font
          ctx.font = 'italic 31.5px Poppins, sans-serif'; // Increased from 28px to 31.5px (28 + 3.5)
          const tagline = userData.tagline || 'Your Tagline';
          // Remove any asterisks from the tagline for cleaner display
          const cleanTagline = tagline.replace(/\*/g, '');
          const words = cleanTagline.split(' ');
          let line = '';
          let y = 450;
          
          words.forEach(word => {
            const testLine = line + word + ' ';
            const metrics = ctx.measureText(testLine);
            if (metrics.width > canvas.width - 100 && line !== '') {
              ctx.fillText(line, canvas.width / 2, y);
              line = word + ' ';
              y += 40;
            } else {
              line = testLine;
            }
          });
          ctx.fillText(line, canvas.width / 2, y);
          
          // Add L&T branding
          ctx.font = 'bold 36px Poppins, sans-serif';
          ctx.fillText('L&T Finance', canvas.width / 2, canvas.height - 100);
          
          // Add yellow strip at the bottom with URL
          try {
            // Create yellow strip at bottom with reduced width and rounded corners
            const stripHeight = 40; // Height of the strip in pixels
            const stripMargin = 100; // Margin from each side (increased from 70px to 100px as requested)
            const cornerRadius = 10; // Radius for rounded corners
            
            ctx.fillStyle = '#FFC107'; // Yellow color
            
            // Draw rounded rectangle for the strip
            ctx.beginPath();
            // Start from top-left corner with radius
            ctx.moveTo(stripMargin + cornerRadius, canvas.height - stripHeight);
            // Top edge
            ctx.lineTo(canvas.width - stripMargin - cornerRadius, canvas.height - stripHeight);
            // Top-right corner
            ctx.arcTo(canvas.width - stripMargin, canvas.height - stripHeight, canvas.width - stripMargin, canvas.height - stripHeight + cornerRadius, cornerRadius);
            // Right edge
            ctx.lineTo(canvas.width - stripMargin, canvas.height);
            // Bottom-right corner (no rounding)
            ctx.lineTo(stripMargin, canvas.height);
            // Bottom-left corner (no rounding)
            ctx.lineTo(stripMargin, canvas.height - stripHeight + cornerRadius);
            // Top-left corner
            ctx.arcTo(stripMargin, canvas.height - stripHeight, stripMargin + cornerRadius, canvas.height - stripHeight, cornerRadius);
            ctx.closePath();
            ctx.fill();
            
            // Add URL text to the strip
            ctx.fillStyle = '#000000'; // Black text (changed from white for better contrast on yellow)
            ctx.font = 'bold 40px Poppins, sans-serif'; // Further increased font size for better visibility
            ctx.textAlign = 'center';
            ctx.fillText('Visit: www.LTFGAMECHANGER.in', canvas.width / 2, canvas.height - stripHeight/2 + 8); // Centered vertically in the strip
          } catch (stripError) {
            console.error('Error adding URL strip to fallback poster:', stripError);
          }
          
          // Ensure we show 95% progress for the fallback rendering too
          setLoadingProgress(95);
          
        } catch (fallbackError) {
          console.error('Error with fallback rendering:', fallbackError);
          throw fallbackError;
        }
      }
      
      clearTimeout(timeoutId);
      isProcessing = false;
      
      // Final progress - immediately go to 100% right before hiding the loader
      setLoadingProgress(100);
      
      // Track poster generation completion
      try {
        await sessionTracker.updateSessionStage('poster_generated');
      } catch (trackingError) {
        console.error("Session tracking error:", trackingError);
        // Continue even if tracking fails
      }
      
      // Small delay after reaching 100% before hiding the loader
      await new Promise(resolve => setTimeout(resolve, 1200));
      setIsLoading(false);
    } catch (err) {
      console.error('Error in poster generation main process:', err);
      setError('Failed to generate poster. Please try again.');
      clearTimeout(timeoutId);
      isProcessing = false;
      setIsLoading(false);
    }
  }, [processedImage, userData, templatePath]);

  // Modified to wait for template loading - moved AFTER generatePoster definition
  useEffect(() => {
    if (processedImage && userData && templateLoaded) {
      // Only proceed with poster generation after template is loaded
      generatePoster();
    }
  }, [processedImage, userData, templateLoaded, generatePoster]);

  // Function to load user data and image
  const loadUserDataAndImage = async () => {
    try {
      // Load the Poppins font
      const ensurePoppinsFont = () => {
        // Check if the font is already added to the document
        const existingLink = document.querySelector('link[href*="fonts.googleapis.com/css2?family=Poppins"]');
        
        if (!existingLink) {
          // Create link element for Poppins font
          const fontLink = document.createElement('link');
          fontLink.rel = 'stylesheet';
          fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap';
          document.head.appendChild(fontLink);
          
          // Create a span element to trigger font loading
          const span = document.createElement('span');
          span.style.fontFamily = 'Poppins, sans-serif';
          span.style.visibility = 'hidden';
          span.textContent = 'Font loader';
          document.body.appendChild(span);
          
          // Remove the span after a short delay
          setTimeout(() => {
            if (span && document.body.contains(span)) {
              document.body.removeChild(span);
            }
          }, 500);
        }
      };
      
      // Ensure Poppins font is loaded
      ensurePoppinsFont();
      
      // Retrieve user data from session storage
      const storedUserDataStr = sessionStorage.getItem('userData');
      
      // Check if we have user data
      if (!storedUserDataStr) {
        console.error('No user data found in session storage');
        setError('No user data found. Please go back and add your details.');
        setIsLoading(false);
        
        // Navigate back to upload page after a brief delay
        setTimeout(() => {
          navigate('/upload');
        }, 1500);
        
        return;
      }
      
      // Parse user data
      const storedUserData = JSON.parse(storedUserDataStr);
      
      // 🔧 CRITICAL FIX: Re-read company name from sessionStorage to get the latest value
      // This fixes the issue where AddDetails sets companyName after userData is created
      const latestCompanyName = sessionStorage.getItem('companyName');
      if (latestCompanyName && latestCompanyName !== 'null' && latestCompanyName !== 'undefined') {
        storedUserData.companyName = latestCompanyName;
        console.log('🏢 SharePoster - Updated company name from sessionStorage:', latestCompanyName);
      }
      
      // 🧹 CRITICAL: Apply cache cleanup and validation for company data
      console.log('🏢 SharePoster - Before cleanup:', { 
        hasCompanyName: !!storedUserData.companyName,
        companyName: storedUserData.companyName,
        phoneNumber: storedUserData.phoneNumber 
      });
      
      // Check if this user should have company data based on CSV list
      const shouldHaveCompanyByList = CacheCleanup.shouldHaveCompanyData(storedUserData.phoneNumber);
      // Check if company data actually exists (from API or CSV)
      const hasActualCompanyData = storedUserData.companyName && storedUserData.companyName.trim() !== '';
      
      if (!hasActualCompanyData) {
        // Only clean if there's no actual company data
        console.log('🧹 SharePoster - No company data found, cleaning');
        delete storedUserData.companyName;
        delete storedUserData.companyData;
        
        // Also clean from sessionStorage
        CacheCleanup.clearCompanyData(storedUserData.phoneNumber, 'SharePoster-cleanup');
        
        // Update sessionStorage with cleaned data
        sessionStorage.setItem('userData', JSON.stringify(storedUserData));
      } else {
        // Preserve existing company data (from API or CSV)
        console.log('✅ SharePoster - Company data found, preserving:', {
          companyName: storedUserData.companyName,
          isInCSVList: shouldHaveCompanyByList,
          source: shouldHaveCompanyByList ? 'CSV' : 'API'
        });
      }
      
      console.log('🏢 SharePoster - After cleanup:', { 
        hasCompanyName: !!storedUserData.companyName,
        companyName: storedUserData.companyName,
        shouldHaveCompany: shouldHaveCompanyByList 
      });
      
      setUserData(storedUserData);
      
      // Start template loading early - triggered by setting userData
      // No need to wait for the image to start template selection
      
      // Retrieve the processed image directly from session storage
      const storedImageUrl = sessionStorage.getItem('processedImage');
      
      // Check if we have an image URL
      if (!storedImageUrl) {
        console.error('No processed image found in session storage');
        setError('No image found. Please go back and upload your photo.');
        setIsLoading(false);
        
        // Navigate back to upload page after a brief delay
        setTimeout(() => {
          navigate('/upload');
        }, 1500);
        
        return;
      }
      
      
      
      // Handle both blob URLs and data URLs
      if (storedImageUrl.startsWith('blob:') || storedImageUrl.startsWith('data:')) {
        
        setProcessedImage(storedImageUrl);
      } else {
        // Handle other URL formats if needed
        
        setProcessedImage(storedImageUrl);
      }
    } catch (error) {
      console.error('Error loading user data or image:', error);
      setError('There was a problem loading your data. Please try again.');
      setIsLoading(false);
    }
  };
  
  // Load user data and image when component mounts
  useEffect(() => {
    // Initial cleanup on component mount
    console.log('🧹 SharePoster - Initial component mount cleanup');
    CacheCleanup.validateCleanState();
    
    loadUserDataAndImage();
  }, [navigate]);  // Add navigate to the dependency array

  // Load CTA banner images
  useEffect(() => {
    // Set the actual banners from the CTA folder with cache buster
    const cacheBuster = `?v=${Date.now()}`;
    setCtaBanners([
      { id: 'banner1', name: `1copy.png${cacheBuster}` },
      { id: 'banner2', name: `2copy.png${cacheBuster}` },
      { id: 'banner3', name: `3copy.png${cacheBuster}` },
      { id: 'banner4', name: `4copy.png${cacheBuster}` },
      { id: 'banner5', name: `5copy.png${cacheBuster}` }
    ]);
  }, []);

  const handleDownload = async () => {
    if (!generatedPoster) return;
    
    try {
      // Track poster download with session tracker
      await sessionTracker.updateSessionStage('poster_downloaded');
    } catch (trackingError) {
      console.error("Session tracking error:", trackingError);
      // Continue with download even if tracking fails
    }
    
    // Create a temporary link for downloading
    const link = document.createElement('a');
    link.href = generatedPoster;
    link.download = 'lt-finance-poster.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleMakeAnother = () => {
    navigate('/add-details');
  };

  // Check if we're on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Add enhanced resize listener to update mobile state
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
    };

    window.addEventListener('resize', handleResize);
    
    // Call once to ensure state is accurate on initial render
    handleResize();
    
    
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Better mobile scrolling implementation for SharePoster
  useEffect(() => {
    // Only apply minimal fixes if we're on mobile
    if (window.innerWidth <= 768) {
      
      
      // Allow natural scrolling
      document.body.style.overflowX = 'hidden';
      document.body.style.overflowY = 'auto';
      document.body.style.height = 'auto';
      document.body.style.position = 'static';
      
      // Remove any fixed height limitations
      const root = document.getElementById('root');
      if (root) {
        root.style.height = 'auto';
        root.style.minHeight = '100%';
        root.style.overflowY = 'visible';
      }
    }
  }, [isMobile]);

  return (
    <div className="share-poster-page" style={{ backgroundImage: 'url("/images/BG.png")' }}>
      {/* Logo in top left corner with redirect to home */}
      <div className="logo-container">
        <a href="/" title="Go to Home Page">
          <img src="/images/LOGO.png?v=20241118" alt="L&T Finance Logo" className="header-logo" />
        </a>
      </div>
      
      {isLoading ? (
        <div className="loading-overlay">
          <Loader message={loadingStatus} />
          <div className="progress-bar" style={{ 
            width: '80%', 
            margin: '20px auto 0', 
            height: '8px', 
            backgroundColor: 'rgba(255, 255, 255, 0.2)', 
            borderRadius: '4px', 
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div 
              className="progress-fill" 
              style={{ 
                width: `${loadingProgress}%`, 
                height: '100%', 
                backgroundColor: '#0083B5', 
                borderRadius: '4px', 
                transition: 'width 0.8s ease-out'
              }}
            ></div>
          </div>
          <p style={{ 
            textAlign: 'center', 
            marginTop: '8px', 
            fontSize: '14px', 
            color: '#888',
            fontFamily: 'Poppins, sans-serif'
          }}>
            {loadingProgress}% Complete
          </p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button 
            className="make-another-btn" 
            onClick={handleMakeAnother}
          >
            <i className="fas fa-redo-alt"></i> Try Again
          </button>
        </div>
      ) : (
        <>
          <div className="share-poster-content" style={!isMobile ? { marginTop: '40px', marginLeft: '-20px' } : {}}>
            {/* Left side - Poster with base template image */}
            <div className="poster-container" style={!isMobile ? { marginTop: '45px' } : {}}>
              <div className="poster-frame">
                <div className="content-area">
                  {generatedPoster ? (
                    <img 
                      src={generatedPoster} 
                      alt="Your Poster" 
                      className="generated-poster"
                    />
                  ) : (
                    <div className="poster-placeholder">
                      <p>Creating your poster...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Right side - Controls section */}
            <div className="controls-section" style={!isMobile ? { marginLeft: "-25px" } : {}}>
              <h1 className="section-title">Here's Your Gamechanger Moment!</h1>
              <p className="section-subtitle">
                Your poster featuring your image is ready to share, print, flex, and go viral.
              </p>
              
              <button className="download-btn" onClick={handleDownload}>
                <i className="fas fa-download"></i> Download
              </button>
              
              <div className="another-shot">
                <p className="another-shot-text">Wanna give it another shot?</p>
                <button className="make-another-btn" onClick={handleMakeAnother}>
                  <i className="fas fa-redo-alt"></i> Start Over
                </button>
              </div>
            </div>
          </div>
          
          {/* Mobile CTA banners without white container */}
          {isMobile && (
            <div className="mobile-cta-section">
              <CTABannerCarousel banners={ctaBanners} />
            </div>
          )}
        </>
      )}
      
      {/* Half-cut white container with CTA banners - only for desktop */}
      {!isMobile && (
        <div className="cta-container">
          <CTABannerCarousel banners={ctaBanners} />
        </div>
      )}
      
      {/* Hidden canvases for poster generation */}
      <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
    </div>
  );
};

export default SharePoster;